/**
 * FillMap — the fill-campaign's N-dimensional "database building out" view (FXC-9009).
 *
 * A pairwise scatter matrix over the campaign's RANGE axes, framed by the DECLARED box
 * (not the data hull — the empty frame at the start is the point: you watch Tailor fill
 * it). Each solved point lands as a dot shaded by its CL; cache hits are hollow, failed
 * points are crossed, and when a replay cursor (`upTo`) is given only the points up to
 * the cursor exist — the current one pulses. One range axis renders as a single strip,
 * two as one cell, k as the lower triangle of the k×k matrix.
 *
 * Pure and props-driven like the rest of the workbench (campaignMath supplies the steps,
 * the caller supplies the palette), so it renders identically in the hero replay overlay
 * and FillPanel's running card, and tests exercise it without the surface.
 */
import { useMemo } from "react";

import type { CampaignAxisSpec } from "../../../api/highliftCampaign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import { FILL_AXES } from "./campaignMath";
import type { ReplayStep } from "./campaignMath";

const MONO = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** canonical axis name → how to read it off a ReplayStep. */
const STEP_VALUE: Record<string, (s: ReplayStep) => number> = {
  alpha_deg: (s) => s.alphaDeg,
  flap_deploy_deg: (s) => s.deployDeg,
  flaperon_deflection_deg: (s) => s.flaperonDeg,
  mach: (s) => s.mach,
  re_chord_millions: (s) => s.reMillions,
  disk_thrust_loading: (s) => s.blowing,
};

const AXIS_LABEL: Record<string, string> = Object.fromEntries(
  FILL_AXES.map((ax) => [ax.canonical, ax.label]),
);

interface RangeAxis {
  canonical: string;
  label: string;
  min: number;
  max: number;
  value: (s: ReplayStep) => number;
}

export function fillMapAxes(axes: CampaignAxisSpec[]): RangeAxis[] {
  return axes
    .filter(
      (ax) =>
        ax.mode === "range" &&
        typeof ax.min === "number" &&
        typeof ax.max === "number" &&
        ax.max > ax.min &&
        STEP_VALUE[ax.axis] != null,
    )
    .map((ax) => ({
      canonical: ax.axis,
      label: AXIS_LABEL[ax.axis] ?? ax.axis,
      min: ax.min as number,
      max: ax.max as number,
      value: STEP_VALUE[ax.axis]!,
    }));
}

const CELL = 132;
const PAD = 10; // inner padding so edge points don't clip

function frac(v: number, lo: number, hi: number): number {
  return Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
}

export function FillMap({
  steps,
  upTo,
  axes,
  coverage,
  total,
  palette,
  onPickStep,
}: {
  steps: ReplayStep[];
  /** Replay cursor — only steps[0..upTo] exist; null/undefined shows all. */
  upTo?: number | null;
  axes: CampaignAxisSpec[];
  /** Box coverage 0..1 to caption (live state or the replay's crossed iteration). */
  coverage?: number | null;
  /** Caption denominator override: steps EXCLUDE pending points, so a live fill
   *  passes the campaign's dispatched count — else a half-in-flight batch reads
   *  as complete. Replay mounts omit it (the snapshot is the whole story). */
  total?: number | null;
  palette: HighliftPalette;
  /** Click a landed dot → the caller selects its run node (provenance, FXC-9009).
   *  Only dots with a registered run_path are clickable; failed ✕s are not. */
  onPickStep?: (step: ReplayStep) => void;
}) {
  const rangeAxes = useMemo(() => fillMapAxes(axes), [axes]);
  const visible = upTo == null ? steps : steps.slice(0, Math.max(0, upTo + 1));
  // CL shading normalized over the WHOLE campaign so a dot's shade is stable
  // as later points land (a replay must not recolor history).
  const cls = steps.map((s) => s.CL).filter((v): v is number => v != null);
  const clLo = cls.length ? Math.min(...cls) : 0;
  const clHi = cls.length ? Math.max(...cls) : 1;

  if (rangeAxes.length === 0) return null;

  // Lower-triangle pairs: (row axis i, col axis j) for i > j. One axis → a strip.
  const pairs: Array<{ x: RangeAxis; y: RangeAxis | null }> =
    rangeAxes.length === 1
      ? [{ x: rangeAxes[0]!, y: null }]
      : rangeAxes.flatMap((yAx, i) =>
          rangeAxes.slice(0, i).map((xAx) => ({ x: xAx, y: yAx })),
        );

  const dot = (
    step: ReplayStep,
    idx: number,
    x: number,
    y: number,
  ): React.ReactNode => {
    const isCurrent = upTo != null && idx === upTo;
    if (step.failed) {
      return (
        <g key={idx} stroke={palette.warn} strokeWidth={1.4}>
          <line x1={x - 3} y1={y - 3} x2={x + 3} y2={y + 3} />
          <line x1={x - 3} y1={y + 3} x2={x + 3} y2={y - 3} />
        </g>
      );
    }
    // All-equal CLs (a one-point fill, identical cache hits) shade at the same
    // midpoint as unknown-CL dots — never FAINTER than "unknown" (the legend
    // would read backwards on the first solved point of every campaign).
    const t = step.CL == null || clHi === clLo ? 0.5 : frac(step.CL, clLo, clHi);
    const fill = palette.blue;
    const opacity = 0.35 + 0.65 * t;
    // A dot is a door when its run node is registered (FXC-9009 provenance).
    const clickable = Boolean(onPickStep && step.runPath);
    return (
      <g
        key={idx}
        data-testid="fill-map-dot"
        onClick={clickable ? () => onPickStep!(step) : undefined}
        style={clickable ? { cursor: "pointer" } : undefined}
      >
        <title>
          {(step.CL != null ? `CL ${step.CL.toFixed(3)}` : "CL pending") +
            (step.cached ? " · cache hit" : step.backend ? ` · ${step.backend}` : "") +
            (clickable ? " · click → run node" : "")}
        </title>
        {step.cached ? (
          <circle cx={x} cy={y} r={3.4} fill="none" stroke={fill} strokeWidth={1.5} opacity={opacity} />
        ) : (
          <circle cx={x} cy={y} r={3.4} fill={fill} opacity={opacity} />
        )}
        {clickable ? <circle cx={x} cy={y} r={7} fill="transparent" /> : null}
        {isCurrent ? (
          <circle cx={x} cy={y} r={7} fill="none" stroke={palette.primary} strokeWidth={1.5}>
            <animate attributeName="r" values="5;9;5" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.25;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
        ) : null}
      </g>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="fill-map">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {pairs.map(({ x, y }, p) => (
          <svg
            key={p}
            width={CELL}
            height={y ? CELL : 46}
            style={{ background: palette.surface2, borderRadius: 6, border: `1px solid ${palette.line}` }}
          >
            {visible.map((s, i) =>
              dot(
                s,
                i,
                PAD + frac(x.value(s), x.min, x.max) * (CELL - 2 * PAD),
                y ? CELL - PAD - frac(y.value(s), y.min, y.max) * (CELL - 2 * PAD) : 23,
              ),
            )}
            <text x={CELL / 2} y={y ? CELL - 2 : 44} textAnchor="middle" fontSize={8.5} fontFamily={MONO} fill={palette.ink3}>
              {x.label}
            </text>
            {y ? (
              <text x={7} y={CELL / 2} textAnchor="middle" fontSize={8.5} fontFamily={MONO} fill={palette.ink3} transform={`rotate(-90 7 ${CELL / 2})`}>
                {y.label}
              </text>
            ) : null}
          </svg>
        ))}
      </div>
      <div style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink3, display: "flex", gap: 10 }}>
        <span>
          {visible.filter((s) => !s.failed).length}/{total ?? steps.length} points in the box
        </span>
        {coverage != null && Number.isFinite(coverage) ? (
          <span>coverage {(Math.max(0, Math.min(1, coverage)) * 100).toFixed(0)}%</span>
        ) : null}
        <span style={{ marginLeft: "auto" }}>shade = CL · hollow = cached · ✕ = failed</span>
      </div>
    </div>
  );
}
